import { z } from 'zod';
import { QUOTATION_STATUS_OPTIONS } from './quotationFormConstants';
import { quotationFormGroupRowSchema } from './quotationFormGroupSchema';

const companyBranchJobCodes = ['GL', 'GLJ', 'GLM'] as const;

const quotationFormStatusEnum = z.enum(
  QUOTATION_STATUS_OPTIONS.map((o) => o.value) as [
    (typeof QUOTATION_STATUS_OPTIONS)[number]['value'],
    ...(typeof QUOTATION_STATUS_OPTIONS)[number]['value'][],
  ],
);

const samplePickupEnum = z.enum(['sampling', 'diantar']);

/** Grid row schema: save-time business rules remain in Samples / Biaya helpers. */
export const quotationFormSampleRowSchema = z.object({
  key: z.string(),
  documentId: z.string().optional(),
  jenisPengukuranMasterId: z.string(),
  jenisPengukuranLabel: z.string().optional(),
  parameterUji: z.string(),
  bakuMutuMasterId: z.string(),
  bakuMutuLabel: z.string().optional(),
  jumlahTitik: z.string(),
  harga: z.string(),
});

export const quotationFormOtherCostRowSchema = z.object({
  key: z.string(),
  documentId: z.string().optional(),
  description: z.string(),
  quantity: z.string(),
  cost_price: z.string(),
});

export function createQuotationFormSchema(isCreate: boolean) {
  return z
    .object({
      quotation_number: z.string(),
      quotation_date: z.string(),
      company_branch: z
        .object({
          documentId: z.string().min(1),
          name: z.string(),
          address: z.string().nullable().optional(),
          job_code: z.enum(companyBranchJobCodes),
        })
        .nullable(),
      preorder: z.boolean(),
      progress_status: quotationFormStatusEnum,
      is_contract: z.boolean(),
      is_consultant_quote: z.boolean(),
      contract_period_start_date: z.string(),
      contract_period_end_date: z.string(),
      sample_pickup_method: samplePickupEnum,
      assignee_user: z.nullable(z.unknown()),
      approved_by_user: z.nullable(z.unknown()),
      discount_amount: z.string(),
      discount_is_percentage: z.boolean(),
      discount_percentage: z.string(),
      is_ppn: z.boolean(),
      term_of_payment: z.boolean(),
      term_of_payment_date: z.string(),
      term_of_payment_note: z.string(),
      otherCostRows: z.array(quotationFormOtherCostRowSchema),
      customer: z.nullable(
        z
          .object({
            documentId: z.string(),
            customer_number: z.string(),
            name: z.string(),
          })
          .passthrough(),
      ),
      sampleRows: z.array(quotationFormSampleRowSchema),
      sampleGroups: z.array(quotationFormGroupRowSchema),
    })
    .superRefine((data, ctx) => {
      if (!data.customer?.documentId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Pelanggan wajib dipilih.',
          path: ['customer'],
        });
      }
      if (!data.company_branch?.documentId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Cabang wajib dipilih.',
          path: ['company_branch'],
        });
      }
      if (!isCreate && !data.quotation_number.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Nomor quotation wajib diisi.',
          path: ['quotation_number'],
        });
      }
      const dTrim = data.discount_amount.trim();
      if (data.discount_is_percentage) {
        const pTrim = data.discount_percentage.trim();
        if (pTrim === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Persentase diskon wajib diisi; gunakan 0 jika tidak ada.',
            path: ['discount_percentage'],
          });
        } else {
          const p = Number.parseFloat(pTrim.replace(',', '.'));
          if (!Number.isFinite(p) || p < 0 || p > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Persentase diskon harus angka 0–100.',
              path: ['discount_percentage'],
            });
          }
        }
      } else {
        if (dTrim === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Diskon (rupiah) wajib diisi; gunakan 0 jika tidak ada.',
            path: ['discount_amount'],
          });
        } else {
          const n = Number.parseInt(dTrim, 10);
          if (!Number.isFinite(n) || n < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Diskon harus bilangan bulat ≥ 0.',
              path: ['discount_amount'],
            });
          }
        }
      }
      if (data.term_of_payment && !data.term_of_payment_date.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Tanggal term of payment wajib diisi jika syarat pembayaran aktif.',
          path: ['term_of_payment_date'],
        });
      }
      if (data.is_contract) {
        if (!data.contract_period_start_date.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Tanggal mulai kontrak wajib diisi.',
            path: ['contract_period_start_date'],
          });
        }
        const endTrim = data.contract_period_end_date.trim();
        if (!endTrim) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Tanggal kontrak selesai wajib diisi.',
            path: ['contract_period_end_date'],
          });
        } else if (data.contract_period_start_date.trim()) {
          const start = new Date(data.contract_period_start_date.trim());
          const end = new Date(endTrim);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Tanggal kontrak selesai harus setelah tanggal mulai kontrak.',
              path: ['contract_period_end_date'],
            });
          }
        }
      }
    });
}
